import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { v4 as uuid } from 'uuid';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableRowProps,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpOutlineIcon,
  OpenInNew as OpenInNewIcon
} from '@material-ui/icons';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import {
  api,
  ConfigFieldControl,
  ConfigTextField,
  FetchError,
  useLocalCache,
  useSort
} from '@sierralabs/core-ui';
import { useEntityContext } from '@sierralabs/entity-ui';
import {
  mergeExpressionMaps,
  Query,
  QueryExpressionMap,
  QueryManager
} from '@sierralabs/query-core';
import {
  applyQueryFilters,
  defaultQueryBuilderPlugins,
  getQuerySelectValue,
  isPaginatedList,
  useQueryContext,
  useRunQuery
} from '@sierralabs/query-ui';
import { Component } from '@sierralabs/site-core';
import {
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  SiteQueryPicker,
  SiteRouteAndParamPicker,
  SiteSearchPlugin,
  SiteSearchProps,
  SiteTablePlugin,
  SiteTableProps,
  useConfigComponent,
  useFindNearestDescendant,
  useRouteLocalCacheKey,
  useSiteContext,
  useSiteTablePagination,
  useSubscribeComponentChanges
} from '@sierralabs/site-ui';

import ConfirmDialog from '../common/dialogs/ConfirmDialog';
import DragArea from '../common/views/DragArea';
import AppSnackbar from '../common/AppSnackbar';
import { useDownloadRequest } from '../common/blob.hook';
import WrappedTableCell from '../common/WrappedTableCell';
import { useFileUploader } from '../secure-portal/documents/components/DocumentFolderUpload';
import { parseFilePath } from '../secure-portal/documents/Document.helpers';
import { createProxyUrl } from '../secure-portal/documents/Document.hook';
import { parseTaskStatus } from '../secure-portal/tasks/task.helper';

export interface ExportItemQuery {
  // auto generated id for loop keys
  id: string;
  queryId?: string;
  // wrapper syntax within document (e.g. {users}...{/users})
  templateSyntax?: string;
  returnType?: 'paginated' | 'one';
  includeFilters?: boolean;
  filter?: { field?: string; routeId?: string; routeParam?: string };
}

export interface ExportCsvFieldConfig {
  displayName?: string;
}

export interface ExportCsvFieldConfigMap {
  [queryField: string]: ExportCsvFieldConfig | undefined;
}
export interface ExportItem {
  // auto generated id for loop keys
  id: string;
  name: string;
  key?: string; // storageKey
  queries?: ExportItemQuery[];
  csvExportEnabled?: boolean;
  csvExportConfig?: {
    includeFieldLabels?: boolean;
    queryFields?: string[];
    fieldConfig?: ExportCsvFieldConfigMap;
    taskStatusFields?: string[];
  };
}

interface ExportFileDto {
  key: string; // storageKey
  ext?: 'docx' | 'pdf';
  token?: string;
  timeZone: string;
  queries: {
    id: string;
    templateSyntax: string;
    returnType?: 'paginated' | 'one';
    expressionMap?: QueryExpressionMap;
  }[];
}

function getExportUrl(item: Partial<ExportItem>) {
  const storageKey = item.key || '';
  const extension = parseFilePath(storageKey).ext.replace('.', '');
  const proxyUrl = createProxyUrl(
    storageKey,
    undefined,
    false,
    `${item.name || 'Untitled'}.${extension}`
  ).toString();
  const fileProxyUrl = item.key ? proxyUrl : undefined;
  return {
    proxyUrl: fileProxyUrl,
    openFile: () => {
      if (fileProxyUrl) {
        console.debug('[SiteExportButtonPlugin] proxy url:', fileProxyUrl);
        window.open(fileProxyUrl, '_blank');
      }
    }
  };
}

function canExport(item: ExportItem) {
  const hasConfiguriedQueries = (item.queries || []).every(
    query => query.queryId && query.templateSyntax
  );
  // only check for configured queries if CSV export is enabled
  // since template is not required for that export
  return item.csvExportEnabled
    ? hasConfiguriedQueries
    : !!item.key && hasConfiguriedQueries;
}

const StyledDot = styled.div`
  height: 6px;
  min-width: 6px;
  border-radius: 100%;
  margin-right: 10px;
  margin-bottom: 1px;
  &.error {
    background-color: ${props => alpha(props.theme.palette.error.light, 0.8)};
  }
  &:not(.error) {
    background-color: ${props => props.theme.palette.text.disabled};
  }
`;

const SecondaryText: React.FC = props => {
  return (
    <Typography display='inline' variant='body2' color='textSecondary'>
      {props.children}
    </Typography>
  );
};

const ViewExportFile: React.FC<{
  item: ExportItem;
  small?: boolean;
}> = props => {
  const { item, small } = props;
  const { proxyUrl, openFile } = getExportUrl(item);
  const isDisabled = !proxyUrl;
  return (
    <Tooltip
      title={
        isDisabled ? 'Query or File not configured' : 'Open file in new tab'
      }
    >
      <span>
        <IconButton
          size='small'
          disabled={isDisabled}
          onClick={event => {
            event.stopPropagation();
            openFile();
          }}
        >
          <OpenInNewIcon fontSize={small ? 'small' : undefined} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export interface ExportButtonProps
  extends Partial<Pick<ButtonProps, 'color' | 'size' | 'variant'>> {
  text?: string;
  items?: ExportItem[];
}

export const ExportButton: React.FC<
  SiteComponentProps<ExportButtonProps>
> = props => {
  const params = useParams<Record<string, any>>();
  const component = useSubscribeComponentChanges(ExportButtonPlugin, props);
  const { isHover, hoverProps } = useSiteContext();
  const exportNameRef = useRef('');
  const [search] = useQueryParam('search', withDefault(StringParam, ''));
  const [error, setError] = useState<FetchError>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [csvExportProps, setCsvExportProps] = useState<{
    item: ExportItem;
    query: Partial<Query>;
    expressionMap?: QueryExpressionMap;
  }>();
  const { queries: contextQueries } = useQueryContext();
  // for export filters, check parent for SiteSearch component
  const searchComponent = useFindNearestDescendant<SiteSearchProps>(
    SiteSearchPlugin.type,
    component.parent
  );
  const tableComponent = useFindNearestDescendant<SiteTableProps>(
    SiteTablePlugin.type,
    component.parent?.parent
  );
  const searchQueryId = searchComponent?.queryIds?.[0];
  const tableQueryId = tableComponent?.queryIds?.[0];
  const localCacheKey = useRouteLocalCacheKey(component, {
    queryId: searchQueryId
  });
  const { value: searchQuery } = useLocalCache<Partial<Query> | undefined>(
    localCacheKey,
    () => undefined
  );
  // SiteTable should set `sortWithQueryParams` to `true`
  // to properly apply pagination filters for export
  const { pagination } = useSiteTablePagination({ asQueryParams: true });
  const {
    isLoading: isDownloading,
    error: downloadError,
    download
  } = useDownloadRequest();
  const classes = ['site-export-button'];
  if (isHover(component)) {
    classes.push('component-hover');
  }
  const { text, size, color, variant, items = [] } = component.props || {};
  // do loading if csvExportProps is present
  const isLoading = isDownloading || !!csvExportProps;

  const onSelect = (item: ExportItem) => {
    if (isLoading) {
      return;
    }
    setAnchorEl(null);

    const expressionMap = searchQuery?.config;
    const queries: ExportFileDto['queries'] = (item.queries || []).map(
      query => {
        const item: ExportFileDto['queries'][number] = {
          id: query.queryId || '',
          templateSyntax: query.templateSyntax || ''
        };
        if (query.returnType) {
          item.returnType = query.returnType;
        }
        const { filter } = query;
        if (filter && filter.field && filter.routeParam) {
          const filterTarget = params[filter.routeParam];
          item.expressionMap = {
            queryType: 'select',
            wheres: [
              {
                type: 'and',
                condition: {
                  operator: 'equal',
                  parameters: [filter.field, filterTarget]
                }
              }
            ]
          };
        }
        // include filters if this query is the same
        // configured query from SiteSearch component
        if (query.includeFilters) {
          if (expressionMap && searchQueryId && searchQueryId === item.id) {
            item.expressionMap = item.expressionMap
              ? mergeExpressionMaps(item.expressionMap, expressionMap)
              : expressionMap;
          }
          // use search fields of existing table component with the same query
          const searchPaths =
            tableQueryId && tableQueryId === item.id
              ? tableComponent?.props?.searchFields
              : undefined;
          // always apply pagination even without existing expressionMap
          item.expressionMap = applyQueryFilters(
            item.expressionMap || { queryType: 'select' },
            {
              ...pagination,
              search,
              searchPaths,
              paginated: item.returnType === 'paginated'
            }
          );
        }
        return item;
      }
    );
    if (item.csvExportEnabled) {
      // get first configured query for CSV export
      const query = contextQueries.find(query => query.id === queries[0].id);
      if (query) {
        setCsvExportProps({
          item,
          query,
          expressionMap: queries[0].expressionMap
        });
      } else {
        setError({
          statusCode: 404,
          error: 'Not Found',
          message: 'Query not found.'
        });
      }
      return;
    }
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dto: ExportFileDto = { key: item.key || '', timeZone, queries };
    exportNameRef.current = `${item.name}.docx`;
    const request = api.post('components/plugins/export', dto);
    download(`${item.name}.docx`, { request });
  };

  useEffect(() => {
    downloadError && setError(downloadError);
  }, [downloadError]);

  return (
    <SiteComponent plugin={ExportButtonPlugin} {...props}>
      <Button
        size={size}
        disabled={isLoading}
        color={color || 'primary'}
        variant={variant || 'contained'}
        className={classes.join(' ')}
        endIcon={
          isLoading ? (
            <CircularProgress color='primary' size={20} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
        {...hoverProps(component)}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        {text || 'Export'}
      </Button>
      <Menu
        keepMounted
        open={!!anchorEl}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        id={`site-export-menu-${component.id}`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
      >
        {items.length === 0 && (
          <MenuItem disabled>No items configured.</MenuItem>
        )}
        {items.map(item => (
          <MenuItem
            key={item.id}
            disabled={isLoading || !canExport(item)}
            onClick={() => onSelect(item)}
          >
            {item.name || <SecondaryText>Untitled</SecondaryText>}
          </MenuItem>
        ))}
      </Menu>
      {csvExportProps && (
        <CsvExport
          includeFieldLabels={
            csvExportProps.item.csvExportConfig?.includeFieldLabels
          }
          query={csvExportProps.query}
          expressionMap={csvExportProps.expressionMap}
          returnType={csvExportProps.item.queries?.[0].returnType}
          selectedFields={
            csvExportProps.item.csvExportConfig?.queryFields || []
          }
          fieldConfig={csvExportProps.item.csvExportConfig?.fieldConfig}
          taskStatusFields={
            csvExportProps.item.csvExportConfig?.taskStatusFields || []
          }
          onError={error => {
            setError(error);
            setCsvExportProps(undefined);
          }}
          download={csvData => {
            setCsvExportProps(undefined);
            const a = document.createElement('a');
            const blob = new Blob([csvData], {
              type: 'text/csv;charset=utf-8'
            });
            a.textContent = 'download';
            const fileName =
              csvExportProps.item.name ||
              csvExportProps.item.queries?.[0].templateSyntax ||
              '';
            a.download = `${fileName}.csv`;
            a.href = URL.createObjectURL(blob);
            a.click();
            a.href = '#';
          }}
        />
      )}
      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message || 'Unable to export document.'}
      </AppSnackbar>
    </SiteComponent>
  );
};

export const ExportButtonConfigurator: React.FC<
  SiteComponentConfigProps<ExportButtonProps>
> = props => {
  const { component, update } = useConfigComponent(props.component);
  const [open, setOpen] = useState(false);
  const items = component.props?.items || [];
  return (
    <>
      <Box>
        <Typography variant='overline'>Button</Typography>
      </Box>
      <ConfigFieldControl>
        <ConfigTextField
          fullWidth
          label='Title'
          value={component.props?.text || ''}
          onChange={event => update('props.text', event.target.value)}
        />
      </ConfigFieldControl>
      <ConfigFieldControl>
        <ConfigTextField
          select
          fullWidth
          label='Variant'
          value={component.props?.variant || ''}
          onChange={event => update('props.variant', event.target.value)}
        >
          {['contained', 'outlined', 'text'].map(variant => (
            <MenuItem key={variant} value={variant}>
              {variant}
            </MenuItem>
          ))}
        </ConfigTextField>
      </ConfigFieldControl>
      <ConfigFieldControl>
        <ConfigTextField
          select
          fullWidth
          label='Size'
          value={component.props?.size || ''}
          onChange={event => update('props.size', event.target.value)}
        >
          {['large', 'medium', 'small'].map(size => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </ConfigTextField>
      </ConfigFieldControl>
      <ConfigFieldControl>
        <ConfigTextField
          select
          fullWidth
          label='Color'
          value={component.props?.color || ''}
          onChange={event => update('props.color', event.target.value)}
        >
          {['primary', 'secondary'].map(color => (
            <MenuItem key={color} value={color}>
              {color}
            </MenuItem>
          ))}
        </ConfigTextField>
      </ConfigFieldControl>

      <Box mt={1}>
        <Typography variant='overline'>Menu</Typography>
      </Box>
      {items.length === 0 ? (
        <Box mb={1}>
          <SecondaryText>No items configured.</SecondaryText>
        </Box>
      ) : (
        <List dense>
          {items.map(item => (
            <ListItem key={item.id} style={{ paddingLeft: 0 }}>
              <StyledDot className={!canExport(item) ? 'error' : undefined} />
              <ListItemText>
                {item.name || <SecondaryText>Untitled</SecondaryText>}
              </ListItemText>
              <ListItemSecondaryAction>
                <ViewExportFile small item={item} />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Button
        fullWidth
        size='small'
        color='primary'
        variant='outlined'
        onClick={() => setOpen(true)}
      >
        Configure Items
      </Button>

      {open && (
        <ExportItemsDialogConfigurator
          open
          items={items}
          component={component}
          onClose={() => setOpen(false)}
          onChange={items => update('props.items', items)}
        />
      )}
    </>
  );
};

export interface ExportItemsDialogConfiguratorProps {
  open: boolean;
  items: ExportItem[];
  component: Component<ExportButtonProps>;
  onClose?: () => void;
  onChange?: (items: ExportItem[]) => void;
}

export const ExportItemsDialogConfigurator: React.FC<
  ExportItemsDialogConfiguratorProps
> = props => {
  const { open, component, items: initialItems, onClose, onChange } = props;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const didChangeRef = useRef(false);
  const dragChangeRef = useRef({ sourceIndex: -1, targetIndex: -1 });
  const [items, setItems] = useState(initialItems);
  const [updateIndex, setUpdateIndex] = useState(-1);
  const [removeIndex, setRemoveIndex] = useState(-1);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [dragItem, setDragItem] = useState<ExportItem>();
  const sort = useSort(items, item => item.id);
  const sensors = useSensors(useSensor(PointerSensor));
  const removeItem = removeIndex > -1 ? items[removeIndex] : undefined;

  const clearDragChange = () => {
    dragChangeRef.current.sourceIndex = -1;
    dragChangeRef.current.targetIndex = -1;
  };

  useEffect(() => {
    if (!didChangeRef.current) {
      didChangeRef.current = true;
      return;
    }
    onChangeRef.current?.(items);
  }, [items]);

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={onClose}>
      <DialogTitle>Configure Export Items</DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        <Box mx={3}>
          <DialogContentText>Manage export menu items</DialogContentText>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 0 }} />
                <TableCell>Name</TableCell>
                <TableCell>Queries</TableCell>
                <TableCell>Template</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <DndContext
              sensors={sensors}
              onDragStart={event => {
                // prevent dragging if currently dragging an item
                if (dragItem) {
                  return;
                }
                const index = items.findIndex(
                  item => item.id === event.active.id
                );
                const item = items[index];
                dragChangeRef.current.sourceIndex = index;
                setDragItem(item);
              }}
              onDragOver={event => {
                const { active, over } = event;
                if (over && active.id !== over.id) {
                  dragChangeRef.current.targetIndex = sort.findIndex(over.id);
                  sort.reorder(active.id, over.id);
                }
              }}
              onDragEnd={() => {
                const { sourceIndex, targetIndex } = dragChangeRef.current;
                if (
                  sourceIndex > -1 &&
                  targetIndex > -1 &&
                  sourceIndex !== targetIndex
                ) {
                  setItems(arrayMove(items, sourceIndex, targetIndex));
                }
                clearDragChange();
                setDragItem(undefined);
              }}
              onDragCancel={() => {
                clearDragChange();
                setDragItem(undefined);
              }}
            >
              <SortableContext
                strategy={verticalListSortingStrategy}
                items={sort.items.map(item => item.id)}
              >
                <TableBody>
                  {sort.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <SecondaryText>No items configured.</SecondaryText>
                      </TableCell>
                    </TableRow>
                  )}
                  {sort.items.map((item, index) => (
                    <SortableExportItemRow
                      key={item.id}
                      item={item}
                      tableRowProps={{
                        hover: true,
                        onClick: () => setUpdateIndex(index)
                      }}
                      onClickUpdate={() => setUpdateIndex(index)}
                      onClickDelete={() => setRemoveIndex(index)}
                    />
                  ))}
                </TableBody>
              </SortableContext>
              <DragOverlay wrapperElement='tbody' style={{ display: 'table' }}>
                {dragItem && <ExportItemRow isDragging item={dragItem} />}
              </DragOverlay>
            </DndContext>
          </Table>
        </TableContainer>

        <Box mt={2} mx={3}>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Add Export Item
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {(openAddDialog || updateIndex > -1) && (
        <SaveExportItemDialog
          open
          component={component}
          item={items[updateIndex]}
          onClose={() => {
            setOpenAddDialog(false);
            setUpdateIndex(-1);
          }}
          onChange={item => {
            setItems(items => {
              const index = items.findIndex(i => i.id === item.id);
              // if adding new non-csv item, skip if has no storage key yet
              if (index === -1) {
                return !item.csvExportEnabled && !item.key
                  ? items
                  : items.concat(item);
              }
              const copy = Array.from(items);
              copy.splice(index, 1, item);
              return copy;
            });
          }}
        />
      )}
      <ConfirmDialog
        open={!!removeItem}
        confirmLabel='Delete File and Item'
        title={`Delete Export Item "${removeItem?.name || '?'}"`}
        message='Are you sure you want to delete this item? This will also deleted the associated file.'
        onCancel={() => setRemoveIndex(-1)}
        onConfirm={async () => {
          setRemoveIndex(-1);
          setItems(items => {
            if (removeIndex === -1) {
              return items;
            }
            const copy = Array.from(items);
            copy.splice(removeIndex, 1);
            return copy;
          });
        }}
      />
    </Dialog>
  );
};

const StyledTableRow = styled(TableRow)`
  overflow: auto;
  vertical-align: top;
  .column-type {
    min-width: 180px;
  }
  &.dragging {
    opacity: 0.8;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
      0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  }
  &.error:not(.dragging) {
    background-color: ${props => alpha(props.theme.palette.error.main, 0.1)};
  }
  .drag-cell {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .drag-handle {
    margin-top: 6px;
    cursor: grab;
    opacity: 0.25;
    transition: opacity 300ms;
    &:hover {
      opacity: 1;
    }
    svg {
      color: #bbb;
    }
  }
  .actions {
    flex-wrap: wrap;
    padding-top: 10px;
    padding-bottom: 8px;
  }
`;

interface ExportItemRowProps {
  item: ExportItem;
  className?: string;
  isDragging?: boolean;
  tableRowProps?: TableRowProps;
  dragHandleProps?: TableCellProps;
  onClickUpdate?: () => void;
  onClickDelete?: () => void;
}

const ExportItemRow: React.FC<ExportItemRowProps> = props => {
  const {
    item,
    className,
    isDragging,
    tableRowProps,
    dragHandleProps,
    onClickUpdate,
    onClickDelete
  } = props;
  const { queries } = useQueryContext();
  const itemQueries = (item.queries || []).map(itemQuery => {
    return {
      item: itemQuery,
      query: queries.find(query => query.id === itemQuery.queryId)
    };
  });
  const classes = className ? [className] : [];
  isDragging && classes.push('dragging');
  !canExport(item) && classes.push('error');
  return (
    <StyledTableRow {...tableRowProps} className={classes.join(' ')}>
      <TableCell {...dragHandleProps} align='center' className='drag-cell'>
        <DragIndicatorIcon className='drag-handle' />
      </TableCell>
      <WrappedTableCell>
        {item.name || <SecondaryText>Untitled</SecondaryText>}
      </WrappedTableCell>
      <WrappedTableCell>
        {itemQueries.length === 0 && <SecondaryText>Not Set</SecondaryText>}
        {itemQueries.map(query => {
          const { templateSyntax } = query.item;
          return (
            <div key={query.item.id}>
              {query.query?.name || <SecondaryText>Not Set</SecondaryText>} (
              {query.item.returnType || 'all'},{' '}
              {!templateSyntax ? '?' : <code>{templateSyntax}</code>})
            </div>
          );
        })}
      </WrappedTableCell>
      <TableCell className={item.key ? 'actions' : undefined}>
        {item.key ? (
          <ViewExportFile item={item} />
        ) : (
          <SecondaryText>Not Set</SecondaryText>
        )}
      </TableCell>
      <TableCell className='actions'>
        <IconButton
          size='small'
          onClick={event => {
            event.stopPropagation();
            onClickUpdate?.();
          }}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size='small'
          onClick={event => {
            event.stopPropagation();
            onClickDelete?.();
          }}
        >
          <CloseIcon />
        </IconButton>
      </TableCell>
    </StyledTableRow>
  );
};

const SortableExportItemRow: React.FC<ExportItemRowProps> = props => {
  const { item, isDragging } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  return (
    <ExportItemRow
      {...props}
      dragHandleProps={{ ...attributes, ...listeners }}
      tableRowProps={{
        ...props.tableRowProps,
        ref: setNodeRef,
        style: {
          opacity: isDragging ? 0.75 : undefined,
          transform: CSS.Translate.toString(transform),
          transition: transition || undefined
        }
      }}
    />
  );
};

const StyledAccordion = styled(Accordion)`
  .MuiAccordionSummary-root,
  .MuiAccordionDetails-root {
    padding-left: 12px;
    padding-right: 12px;
  }
  .MuiAccordionSummary-root.Mui-expanded {
    min-height: unset;
  }
  .MuiAccordionSummary-content {
    margin: 12px 0px;
    align-items: center;
    .remove-button {
      margin-right: 7px;
    }
  }
`;

const FieldConfigContainer = styled.div`
  width: 100%;
`;

const ExportItemQueryConfigurator: React.FC<{
  item: ExportItemQuery;
  expanded?: boolean;
  component: Component<ExportButtonProps>;
  queryFieldOptions: string[];
  onChange?: (item: ExportItemQuery) => void;
  onRemove?: () => void;
  setExpanded?: (expanded: boolean) => void;
}> = props => {
  const {
    item,
    component,
    expanded,
    queryFieldOptions,
    onChange,
    onRemove,
    setExpanded
  } = props;
  const update = (updated: Partial<ExportItemQuery>) => {
    onChange?.({ ...item, ...updated });
  };
  return (
    <StyledAccordion
      expanded={expanded}
      onChange={(_, expanded) => setExpanded?.(expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <IconButton
          size='small'
          className='remove-button'
          onClick={event => {
            event.stopPropagation();
            onRemove?.();
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        {item.templateSyntax ? (
          `${item.templateSyntax} (${item.returnType || 'all'})`
        ) : (
          <SecondaryText>No template syntax</SecondaryText>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              variant='outlined'
              label='Template Syntax'
              placeholder='e.g. users'
              value={item?.templateSyntax || ''}
              onChange={event => update({ templateSyntax: event.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={9}>
                <SiteQueryPicker
                  hideReorder
                  multiple={false}
                  label='Query'
                  variant='outlined'
                  placeholder='Select query for this item'
                  InputProps={{ disableUnderline: undefined }}
                  value={item?.queryId ? [item.queryId] : []}
                  onChange={queries => {
                    update({ queryId: queries.slice(-1).pop()?.id });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  variant='outlined'
                  label='Return Type'
                  value={item?.returnType || 'all'}
                  onChange={event => {
                    const value = event.target.value as
                      | 'paginated'
                      | 'one'
                      | 'all';
                    update({
                      returnType: !value || value === 'all' ? undefined : value
                    });
                  }}
                >
                  <MenuItem value='all'>All</MenuItem>
                  <MenuItem value='paginated'>Paginated</MenuItem>
                  <MenuItem value='one'>One</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='overline'>
              Include Pagination and Filters
            </Typography>
            <Typography variant='body1' color='textSecondary'>
              When enabled, filters from a{' '}
              <strong>{SiteSearchPlugin.name}</strong> component that has the
              same configured query will be applied.
            </Typography>
            <Collapse in={!!item.includeFilters} style={{ minHeight: 'unset' }}>
              <Box mt={1}>
                <Alert severity='warning'>
                  Make sure that if one main{' '}
                  <strong>{SiteTablePlugin.name}</strong> component exists on
                  the page (e.g. list page) that it has{' '}
                  <strong>Sort with Query Params</strong> configuration{' '}
                  <strong>enabled</strong> to properly apply the pagination for
                  export.
                </Alert>
              </Box>
            </Collapse>
            <ConfigFieldControl>
              <FormControlLabel
                label='Handle Pagination and Filters'
                control={
                  <Checkbox
                    size='small'
                    color='primary'
                    className='checkbox'
                    checked={!!item.includeFilters}
                    onChange={(_, checked) => {
                      update({ includeFilters: checked });
                    }}
                  />
                }
              />
            </ConfigFieldControl>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='overline'>Filter by Route Param</Typography>
            <Typography variant='body1' color='textSecondary'>
              The selected <strong>route param</strong> will be checked against
              the selected <strong>field</strong>.
            </Typography>
            <SiteRouteAndParamPicker
              nearestOnly
              component={component}
              routeId={item.filter?.routeId}
              routeParam={item.filter?.routeParam}
              siteRoutePickerProps={{ label: 'Route', variant: 'outlined' }}
              textFieldProps={{
                variant: 'outlined',
                InputProps: { disableUnderline: undefined } as any
              }}
              onChange={value => {
                update({
                  filter: {
                    ...item.filter,
                    routeId: value.routeId,
                    routeParam: value.routeParam
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={queryFieldOptions}
              value={item.filter?.field || ''}
              onChange={(_, value) => {
                update({
                  filter: { ...item.filter, field: value || undefined }
                });
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size='small'
                  variant='outlined'
                  label='Select a Field'
                />
              )}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </StyledAccordion>
  );
};

const StyledContainer = styled.div`
  margin-top: 16px;
  padding-left: 24px;
  padding-right: 24px;
  &.hidden {
    display: none;
  }
  .MuiAlert-message {
    width: 100%;
    .alert-content {
      display: flex;
    }
    .alert-text {
      flex: 1;
    }
    .alert-action {
      display: flex;
      align-items: start;
      margin-left: 8px;
    }
  }
  .MuiTypography-body1 {
    font-size: 14px;
  }
  .actions {
    display: flex;
    .MuiButtonBase-root:not(:last-child) {
      margin-right: 8px;
    }
  }
  .title-text {
    font-weight: 600;
  }
  .code-block {
    padding: 8px;
    border-radius: 2px;
    border: 1px solid ${props => props.theme.palette.divider};
    background-color: ${props => props.theme.palette.grey[200]};
  }
  ul {
    margin-top: 8px;
    margin-bottom: 8px;
    padding-left: 24px;
    li:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`;

const StyledAlert = styled(Alert)`
  .MuiAlertTitle-root {
    font-size: 15px;
    font-weight: 600;
  }
`;

const SaveExportItemDialog: React.FC<{
  open: boolean;
  item?: ExportItem;
  component: Component<ExportButtonProps>;
  onClose?: () => void;
  onChange?: (item: ExportItem) => void;
}> = props => {
  const { open, component, item: initialItem, onChange } = props;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const shouldUpdateRef = useRef(false);
  const contentRef = useRef<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [item, setItem] = useState<ExportItem>(
    () => initialItem || { id: uuid(), name: '', queries: [] }
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [removeId, setRemoveId] = useState<string>();
  const [expandedId, setExpandedId] = useState<string>();
  const [expendedFieldConfig, setExpandedFieldConfig] = useState<string>();
  const [isUploading, setIsUploading] = useState(false);
  const { queries: contextQueries } = useQueryContext();
  const uploader = useFileUploader('attachments');
  const { proxyUrl, openFile } = getExportUrl(item);
  const selectedFields = item.csvExportConfig?.queryFields || [];
  const queries = item.queries || [];
  const selectAll = '-- Select All --';
  const uploadItem =
    uploader.items[0]?.status === 'uploaded' ? uploader.items[0] : undefined;
  const removeItem = removeId
    ? item.queries?.find(query => query.id === removeId)
    : undefined;

  const getQueryFields = (queryId: string | undefined) => {
    const query = queryId
      ? contextQueries.find(query => query.id === queryId)
      : undefined;
    return (query?.config?.selects || []).map(select => select.selection);
  };
  const csvQueryFields = getQueryFields(queries[0]?.queryId);

  const isValid = useMemo(() => {
    const hasQueries =
      Array.isArray(item.queries) &&
      item.queries.length > 0 &&
      item.queries.every(query => query.queryId && query.templateSyntax);
    return !!(
      hasQueries &&
      (!item.csvExportEnabled
        ? item.key
        : item.csvExportConfig &&
          Array.isArray(item.csvExportConfig.queryFields) &&
          item.csvExportConfig.queryFields.length > 0)
    );
  }, [item]);

  const onClose = () => {
    if (!isUploading) {
      props.onClose?.();
    }
  };

  const getClassName = (index: number) => {
    const classes = ['tab-content'];
    tabIndex !== index && classes.push('hidden');
    return classes.join(' ');
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setIsUploading(true);
    uploader.clear();
    const uploadedItems = await uploader.uploadAsync(
      Array.from(files).slice(-1)
    );
    const storageKey = uploadedItems[0]?.presignedUrl?.storageKey;
    if (!storageKey) {
      setIsUploading(false);
      return;
    }
    // trigger and force onChange update in useEffect when storageKey is set
    shouldUpdateRef.current = true;
    setIsUploading(false);
    setItem(item => ({ ...item, key: storageKey }));
  };

  const updateFieldConfig = (field: string, updates: ExportCsvFieldConfig) => {
    const fieldConfig = item.csvExportConfig?.fieldConfig || {};
    const config = (fieldConfig[field] = fieldConfig[field] || {});
    Object.assign(config, updates);
    setItem(item => {
      return {
        ...item,
        csvExportConfig: {
          ...item.csvExportConfig,
          fieldConfig
        }
      };
    });
  };

  useEffect(() => {
    if (shouldUpdateRef.current) {
      shouldUpdateRef.current = false;
      onChangeRef.current?.(item);
    }
  }, [item]);

  useLayoutEffect(() => {
    // go to top when tabIndex changes
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [tabIndex]);

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={onClose}>
      {isUploading && <LinearProgress />}
      <DialogTitle>{initialItem ? 'Update' : 'Create'} Export Item</DialogTitle>
      <DialogContent innerRef={contentRef} style={{ padding: 0 }}>
        <Tabs
          value={tabIndex}
          variant='scrollable'
          scrollButtons='auto'
          indicatorColor='primary'
          onChange={(_, value) => setTabIndex(value)}
        >
          <Tab label='Info' />
          <Tab label='Template' />
          <Tab label='CSV Export' />
          <Tab label='Learn More' />
        </Tabs>

        <StyledContainer className={getClassName(0)}>
          <Box mb={2}>
            <Typography variant='body2' color='textSecondary'>
              Set the name and queries for this Export item.
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant='outlined'
            label='Template Name'
            placeholder='Enter the template name'
            value={item.name || ''}
            onChange={event => {
              setItem(item => ({ ...item, name: event.target.value }));
            }}
          />

          <Box my={1}>
            <Typography variant='overline'>Queries</Typography>
          </Box>
          <Box mb={2}>
            {queries.length === 0 && (
              <SecondaryText>Add queries for this Export item.</SecondaryText>
            )}
            {queries.map((query, index) => (
              <ExportItemQueryConfigurator
                item={query}
                key={query.id}
                component={component}
                expanded={query.id === expandedId}
                queryFieldOptions={getQueryFields(query.queryId)}
                setExpanded={expanded => {
                  setExpandedId(expanded ? query.id : undefined);
                }}
                onChange={updated => {
                  setItem(item => {
                    const queries = Array.from(item.queries || []);
                    queries.splice(index, 1, updated);
                    return { ...item, queries };
                  });
                }}
                onRemove={() => setRemoveId(query.id)}
              />
            ))}
          </Box>
          <Box className='actions'>
            <Button
              size='small'
              variant='outlined'
              disabled={
                item.csvExportEnabled && (item.queries || []).length > 0
              }
              startIcon={<AddIcon />}
              onClick={() => {
                const id = uuid();
                setItem(item => {
                  return {
                    ...item,
                    queries: (item.queries || []).concat({ id })
                  };
                });
                setExpandedId(id);
              }}
            >
              Add Query
            </Button>
            <Button
              size='small'
              variant='outlined'
              startIcon={<HelpOutlineIcon />}
              onClick={() => setTabIndex(2)}
            >
              Learn More
            </Button>
            <Button
              size='small'
              color='primary'
              variant='contained'
              endIcon={<ChevronRightIcon />}
              onClick={() => setTabIndex(tabIndex => tabIndex + 1)}
            >
              Next
            </Button>
          </Box>
        </StyledContainer>
        <StyledContainer className={getClassName(1)}>
          <Box mb={1}>
            <Typography variant='body2' color='textSecondary'>
              Upload the Template Document File for this Export item.
            </Typography>
          </Box>
          {proxyUrl && (
            <Box mb={1}>
              <Alert severity={uploadItem ? 'success' : 'info'}>
                <div className='alert-content'>
                  <div className='alert-text'>
                    {uploadItem
                      ? 'Template file uploaded successfully.'
                      : 'Template file uploaded.'}{' '}
                    Drag or upload a new file below to{' '}
                    <strong>delete and replace</strong> the old Template file.
                  </div>
                  <div className='alert-action'>
                    <Button
                      size='small'
                      color='primary'
                      variant='outlined'
                      disabled={!proxyUrl}
                      endIcon={<OpenInNewIcon />}
                      onClick={openFile}
                    >
                      Open File
                    </Button>
                  </div>
                </div>
              </Alert>
            </Box>
          )}
          <DragArea
            dragOver={dragOver}
            dragLabel='Drag document file to upload'
            buttonLabel='Select Document File'
            onUpload={handleFiles}
            setDragOver={setDragOver}
            onClick={() => fileInputRef.current?.click()}
          />
          <Collapse in={!!uploadItem} style={{ minHeight: 'unset' }}>
            <Box mt={2}>
              <Alert severity='success'>File successfully uploaded.</Alert>
            </Box>
          </Collapse>
          <Box mt={2} className='actions'>
            <Button
              size='small'
              color='primary'
              variant='contained'
              startIcon={<ChevronLeftIcon />}
              onClick={() => setTabIndex(tabIndex => tabIndex - 1)}
            >
              Back
            </Button>
            <Button
              size='small'
              color='primary'
              variant='contained'
              endIcon={<ChevronRightIcon />}
              onClick={() => setTabIndex(tabIndex => tabIndex + 1)}
            >
              Next
            </Button>
          </Box>
        </StyledContainer>
        <StyledContainer className={getClassName(2)}>
          <ConfigFieldControl>
            <FormControlLabel
              label='Enable CSV Export'
              control={
                <Checkbox
                  size='small'
                  color='primary'
                  className='checkbox'
                  checked={!!item.csvExportEnabled}
                  onChange={(_, checked) => {
                    setItem(item => ({ ...item, csvExportEnabled: checked }));
                  }}
                />
              }
            />
          </ConfigFieldControl>
          <ConfigFieldControl>
            <FormControlLabel
              label='Include Field Labels'
              disabled={!item.csvExportEnabled}
              control={
                <Checkbox
                  size='small'
                  color='primary'
                  className='checkbox'
                  checked={!!item.csvExportConfig?.includeFieldLabels}
                  onChange={(_, checked) => {
                    setItem(item => {
                      return {
                        ...item,
                        csvExportConfig: {
                          ...item.csvExportConfig,
                          includeFieldLabels: checked
                        }
                      };
                    });
                  }}
                />
              }
            />
          </ConfigFieldControl>
          <Collapse in={item.csvExportEnabled} style={{ minHeight: 'unset' }}>
            <StyledAlert severity='info'>
              <AlertTitle>CSV Export is Enabled</AlertTitle>
              <Typography variant='body2'>
                The <strong>configured template</strong> will not be used for
                this export item and only the{' '}
                <strong>first configured query</strong> will be used for this
                export.
              </Typography>
            </StyledAlert>
          </Collapse>
          <Box mt={1}>
            <Typography variant='overline' className='title-text'>
              Fields to be included in Export
            </Typography>
          </Box>
          <ConfigFieldControl>
            <Autocomplete
              multiple
              fullWidth
              disabled={!item.csvExportEnabled}
              options={[selectAll, ...csvQueryFields]}
              value={item.csvExportConfig?.queryFields || []}
              onChange={(_, values) => {
                // handle select all fields
                const queryFields = values.includes(selectAll)
                  ? Array.from(csvQueryFields)
                  : values;
                setItem(item => {
                  return {
                    ...item,
                    csvExportConfig: { ...item.csvExportConfig, queryFields }
                  };
                });
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size='small'
                  variant='outlined'
                  label='Select CSV Fields'
                />
              )}
            />
          </ConfigFieldControl>
          <Box mt={1}>
            <Typography variant='overline' className='title-text'>
              Task status fields to parse
            </Typography>
          </Box>
          <ConfigFieldControl>
            <Autocomplete
              multiple
              fullWidth
              disabled={!item.csvExportEnabled}
              options={[selectAll, ...csvQueryFields]}
              value={item.csvExportConfig?.taskStatusFields || []}
              onChange={(_, values) => {
                // handle select all fields
                const taskStatusFields = values.includes(selectAll)
                  ? Array.from(csvQueryFields)
                  : values;
                setItem(item => {
                  return {
                    ...item,
                    csvExportConfig: {
                      ...item.csvExportConfig,
                      taskStatusFields
                    }
                  };
                });
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  size='small'
                  variant='outlined'
                  label='Select Fields'
                />
              )}
            />
          </ConfigFieldControl>
          {selectedFields.length > 0 && (
            <>
              <Box mt={1}>
                <Typography variant='overline' className='title-text'>
                  Fields Config
                </Typography>
              </Box>
              <FieldConfigContainer>
                {selectedFields.map((field, index) => {
                  const fieldConfig = item.csvExportConfig?.fieldConfig;
                  const config = fieldConfig?.[field];
                  return (
                    <StyledAccordion
                      key={`${field}-${index}`}
                      expanded={expendedFieldConfig === field}
                      onChange={(_, expanded) => {
                        setExpandedFieldConfig(expanded ? field : undefined);
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>
                          {field}
                          {config?.displayName && (
                            <>
                              {' '}
                              (<strong>{config.displayName}</strong>)
                            </>
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          fullWidth
                          size='small'
                          variant='outlined'
                          label='Display Name'
                          value={config?.displayName || ''}
                          onChange={event =>
                            updateFieldConfig(field, {
                              displayName: event.target.value
                            })
                          }
                        />
                      </AccordionDetails>
                    </StyledAccordion>
                  );
                })}
              </FieldConfigContainer>
            </>
          )}
          <Box mt={2} className='actions'>
            <Button
              size='small'
              color='primary'
              variant='contained'
              startIcon={<ChevronLeftIcon />}
              onClick={() => setTabIndex(tabIndex => tabIndex - 1)}
            >
              Back
            </Button>
          </Box>
        </StyledContainer>
        <StyledContainer className={getClassName(3)}>
          <Box mb={1}>
            <Typography variant='overline' className='title-text'>
              Template Syntax
            </Typography>
          </Box>
          <Box ml={1}>
            <Typography variant='body1'>
              The <strong>template syntax</strong> is the token used for
              accessing the query data in the Template file.
            </Typography>
            <Box my={1}>
              <Divider />
            </Box>
            <Typography variant='body1'>
              Template Syntax example: <strong>users</strong>
            </Typography>
            <Typography variant='body1'>Usage in Template file:</Typography>
            <Typography variant='body1' className='code-block'>
              <code>{'{#users}Name: {firstName} {lastName}{/users}'}</code>
            </Typography>
          </Box>

          <Box my={1}>
            <Typography variant='overline' className='title-text'>
              Return Type
            </Typography>
          </Box>
          <Box ml={1}>
            <Typography variant='body1'>
              Depending on the <strong>return type</strong>, the usage of the{' '}
              <strong>template syntax</strong> will differ.
            </Typography>
            <Box my={1}>
              <Divider />
            </Box>
            <Typography variant='body1'>
              Template Syntax example: <strong>users</strong>
            </Typography>
            <Typography variant='body1'>Return types:</Typography>
            <ul>
              <li>
                <Typography variant='body1'>
                  <strong>all</strong> - this return type returns an array of
                  items based on the query.
                </Typography>
                <Typography variant='body1'>Example:</Typography>
                <Typography variant='body1' className='code-block'>
                  <code>{'{#users}Name: {firstName}{/users}'}</code>
                </Typography>
              </li>
              <li>
                <Typography variant='body1'>
                  <strong>paginated</strong> - this return type changes the
                  return type to this structure:
                </Typography>
                <pre className='code-block'>
                  {'{\n' +
                    '  total: number;\n' +
                    '  data: Data[];\n' +
                    '  page: number;\n' +
                    '  limit: number;\n' +
                    '}'}
                </pre>
                <Typography variant='body1'>Example:</Typography>
                <Typography variant='body1' className='code-block'>
                  <code>{'{#users.data}Name: {firstName}{/users.data}'}</code>
                </Typography>
              </li>
              <li>
                <Typography variant='body1'>
                  <strong>one</strong> - this return type returns one record
                  from the query.
                </Typography>
                <Typography variant='body1'>Example:</Typography>
                <Typography variant='body1' className='code-block'>
                  <code>{'{#users}Name: {firstName}{/users}'}</code>
                </Typography>
              </li>
            </ul>
          </Box>
          <Box mt={2} className='actions'>
            <Button
              size='small'
              color='primary'
              variant='contained'
              startIcon={<ChevronLeftIcon />}
              onClick={() => setTabIndex(tabIndex => tabIndex - 1)}
            >
              Back
            </Button>
          </Box>
        </StyledContainer>

        <input
          hidden
          type='file'
          accept='.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          ref={fileInputRef}
          onChange={event => {
            handleFiles(event.target.files);
            // clear input field
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={isUploading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!isValid}
          onClick={() => {
            onChange?.(item);
            onClose();
          }}
        >
          Save Item
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={!!removeItem}
        confirmLabel='Remove'
        title='Remove Query Item'
        message={`Are you sure you want to remove ${
          removeItem?.templateSyntax ? `"${removeItem.templateSyntax}"` : 'this'
        } query item?`}
        onCancel={() => setRemoveId(undefined)}
        onConfirm={() => {
          setRemoveId(undefined);
          setItem(item => {
            return {
              ...item,
              queries: (item.queries || []).filter(
                query => query.id !== removeId
              )
            };
          });
        }}
      />
    </Dialog>
  );
};

interface CsvExportProps {
  includeFieldLabels?: boolean;
  selectedFields: string[];
  fieldConfig?: ExportCsvFieldConfigMap;
  taskStatusFields: string[];
  query: Partial<Query>;
  returnType: ExportItemQuery['returnType'];
  expressionMap?: QueryExpressionMap;
  download: (csvData: string) => void;
  onError?: (error: FetchError) => void;
}

const CsvExport: React.FC<CsvExportProps> = props => {
  const {
    includeFieldLabels,
    query,
    returnType,
    expressionMap,
    fieldConfig,
    download,
    onError
  } = props;
  const downloadRef = useRef(download);
  downloadRef.current = download;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const { entities } = useEntityContext();
  const {
    queryData,
    runQueryError,
    runQuery,
    runQueryGetOne,
    runPaginatedQuery
  } = useRunQuery();
  const runQueryFn =
    returnType === 'paginated'
      ? runPaginatedQuery
      : returnType === 'one'
      ? runQueryGetOne
      : runQuery;
  // evaluate only when items changed
  const selectedFields = useMemo(() => {
    return props.selectedFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedFields.join(',')]);

  const taskStatusFields = useMemo(() => {
    return props.taskStatusFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.taskStatusFields.join(',')]);

  const queryManager = useMemo(() => {
    return new QueryManager(query, entities || [], defaultQueryBuilderPlugins);
  }, [entities, query]);

  useEffect(() => {
    const config = !query.config
      ? undefined
      : expressionMap
      ? mergeExpressionMaps(query.config, expressionMap)
      : query.config;
    if (config) {
      runQueryFn(config);
    } else {
      onErrorRef.current?.({
        statusCode: 400,
        error: 'Bad Request',
        message: 'Missing Query configuration.'
      });
    }
  }, [query, expressionMap, runQueryFn]);

  useEffect(() => {
    if (runQueryError) {
      onErrorRef.current?.(runQueryError);
    }
  }, [runQueryError]);

  // generate CSV
  useEffect(() => {
    if (!queryData) {
      return;
    }
    // add labels and optional fieldLabels to CSV first row
    const labels: string[] = [];
    const fieldLabels: string[] = [];
    for (const field of selectedFields) {
      const columnMap = queryManager.queryAliasColumnMap.find(
        cm => cm.path === field
      );
      const displayName =
        fieldConfig?.[field]?.displayName || columnMap?.column?.displayName;

      labels.push(displayName || field || '');
      fieldLabels.push(columnMap?.column?.comment || 'N/A');
    }

    // process all data rows
    // filter selects based on selected fields to be exported
    // sort based on the order of selectFields to properly match column data
    const selects = (queryManager.query?.config?.selects || [])
      .filter(select => selectedFields.includes(select.selection))
      .sort((a, b) => {
        const aIndex = selectedFields.indexOf(a.selection);
        const bIndex = selectedFields.indexOf(b.selection);
        return aIndex - bIndex;
      });
    // handle values from all return types
    const data = isPaginatedList(queryData)
      ? queryData.data
      : Array.isArray(queryData)
      ? queryData
      : queryData
      ? [queryData]
      : [];

    const rows = data.map(item => {
      const row = selects.map(select => {
        let value = getQuerySelectValue({
          item,
          select,
          expressionMap: queryManager.query?.config
        });
        if (!value) {
          return '""';
        }
        value = value.toString();
        value = taskStatusFields.includes(select.selection)
          ? parseTaskStatus(value)
          : value;

        return `"${value.replace(/"/g, '""')}"`;
      });
      return row;
    });

    const csvRows = [labels];
    if (includeFieldLabels) {
      csvRows.push(fieldLabels);
    }
    csvRows.push(...rows);
    const csvData = csvRows.map(row => row.join(',')).join('\r\n');
    downloadRef.current(csvData);
  }, [
    includeFieldLabels,
    queryData,
    queryManager,
    selectedFields,
    fieldConfig,
    taskStatusFields
  ]);

  return null;
};

export const ExportButtonPlugin: SiteComponentPlugin<ExportButtonProps> = {
  name: 'Export Button',
  type: 'export-button',
  component: ExportButton,
  configurator: ExportButtonConfigurator,
  initConfig: component => {
    if (!component.props) {
      component.props = {};
    }
    component.props.text = 'Export';
    component.props.color = 'primary';
    component.props.variant = 'contained';
    return component;
  }
};
